import React, { FC, useRef } from 'react';
import classes from './TileItem.module.scss';
import { getTileDef, NodeDef, TileDef } from '../../config/tileMapConfig';
import Meeple from './Meeple';
import { getContainerStyle, getTileCoords, getTileHeight, getTileStyle, getTileWidth } from './TileAsset';
import { TileDTO } from '../../types/ccson_env';
import { TileBasedGameFamilyType } from '../../config/tileGameConfig';
import CcsonNode from './CcsonNode';
import { MeepleDTO, MeepleType } from '@envclient/envengine/src/envs/tilebase/Meeple';
import { CSSPos, Pos } from '../../types/frontendTypes';

function calculateUnrotatedNodePos(meepleNode: NodeDef | undefined, rot: number) {
  const tileWidth = getTileWidth();
  const tileHeight = getTileHeight();
  if (!meepleNode) return;
  const xCentered = meepleNode.x - 0.5;
  const yCentered = meepleNode.y - 0.5;
  const rad = rot * (Math.PI / 180);
  const meepleWidth = 41;
  const meepleHeight = 50;
  const xUnrotated = Math.cos(rad) * xCentered - Math.sin(rad) * yCentered;
  const yUnrotated = Math.sin(rad) * xCentered + Math.cos(rad) * yCentered;
  const left = Math.round((xUnrotated + 0.5) * tileWidth - meepleWidth / 2);
  const top = Math.round((yUnrotated + 0.5) * tileHeight - meepleHeight / 2);
  return { left, top };
}

export { calculateUnrotatedNodePos as calculateMeeplePos };

interface TileItemProps {
  family: TileBasedGameFamilyType;
  tile: TileDTO;
  active: boolean;
  last?: boolean;
  onClick?: (action: TileDTO) => void;
  onDragStart?: (
    tile: TileDTO,
    clientX: number,
    clientY: number,
    rect: DOMRect,
    sourceScale: number,
    pointerId: number
  ) => void;
  onMeepleClick?: (meeple: MeepleDTO) => void;
  posActions?: Array<TileDTO>;
  preferredMeepleType?: MeepleType;
  rotateThreshold?: number;
  tempPos?: CSSPos;
  dragging?: boolean;
}

const TileItem: FC<TileItemProps> = (props) => {
  const { tile, active, last, family, preferredMeepleType, tempPos, dragging, rotateThreshold = 1000 } = props;
  const { x, y, type, rot, meeple } = tile;
  // outer .tileItem box ref - drag offsets are measured against it (it is not rotated)
  const tileItemRef = useRef<HTMLDivElement>(null);

  const tileDef: TileDef = getTileDef(type);
  const nodes = tileDef.nodes;

  const tileWidth = getTileWidth();
  const tileHeight = getTileHeight();

  const findClosestPos = (nativeEvent: globalThis.MouseEvent) => {
    const { posActions, tile } = props;
    const { rot } = tile;
    if (!posActions) return null;
    const nodes = tileDef.nodes;
    let minDistance = 1000000;
    let closestNode;

    function distance2(x1: number, y1: number, x2: number, y2: number) {
      return (x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2);
    }

    for (const node of nodes) {
      if (!getNodeActions(node).length) continue;
      const d = distance2(node.x * tileWidth, node.y * tileHeight, nativeEvent.offsetX, nativeEvent.offsetY);
      if (d < minDistance) {
        minDistance = d;
        closestNode = node;
      }
    }
    if (closestNode && minDistance < rotateThreshold) {
      const closestPos = getNodeActions(closestNode);
      console.log('closestPos', closestPos, meeple);
      // prefer second choice (eg. meeple or giant) if that is preferred type
      if (closestPos.length > 1 && closestPos[1].meeple?.type === preferredMeepleType) {
        return closestPos[1];
      }
      return closestPos[0];
    }
    // try to rotate forw
    let nextRotPos = posActions.find((action: TileDTO) => action.rot > rot);
    //try to rotate backw
    if (!nextRotPos) nextRotPos = posActions.find((action: TileDTO) => action.rot < rot);
    // return if found
    if (nextRotPos) return nextRotPos;
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!props.active || !props.onClick) return;
    const action = findClosestPos(e.nativeEvent);
    if (action) props.onClick(action);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!props.active || !props.onDragStart) return;
    const rect = (tileItemRef.current ?? e.currentTarget).getBoundingClientRect();
    // outer box CSS width === getTileWidth(), so rect.width/that === the rendered scale (zoom)
    const sourceScale = rect.width / getTileWidth() || 1;
    props.onDragStart(tile, e.clientX, e.clientY, rect, sourceScale, e.pointerId);
  };

  // block PrismaZoom panning while interacting with a draggable tile (separate event channel)
  const stopDragPan = (e: React.MouseEvent | React.TouchEvent) => {
    if (!props.active || !props.onDragStart) return;
    e.stopPropagation();
  };

  const handleMeepleClick = (meeple: MeepleDTO) => {
    if (!props.active || !props.onMeepleClick) return;
    props.onMeepleClick(meeple);
  };

  const getNodeActions = (node: NodeDef): TileDTO[] => {
    const { tile, posActions } = props;
    const { rot } = tile;
    if (!posActions) return [];
    return posActions.filter((action) => {
      return action.rot === rot && action.meeple && action.meeple.nodeId === node.id;
    });
  };

  const tileStyle: React.CSSProperties = tempPos ? { ...tempPos } : getTileStyle(x, y);
  // follow the cursor without the placement easing while being dragged
  if (dragging) tileStyle.transition = 'none';

  const coords = getTileCoords(type, family) as Pos;

  const contStyle = getContainerStyle(coords, rot);

  // calc unrotated meeple pos
  let meeplePos;
  if (meeple) {
    const meepleNode = nodes.find((node) => node.id === meeple.nodeId);
    meeplePos = calculateUnrotatedNodePos(meepleNode, rot);
  }

  return (
    <div className={classes.tileItem + ' ' + classes[family as string]} style={tileStyle} ref={tileItemRef}>
      <div
        className={classes.rotCont + ' ' + classes[family as string]}
        style={contStyle}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onMouseDown={stopDragPan}
        onTouchStart={stopDragPan}>
        <div className={classes.innerCont}>
          {last && <div className={classes.last}></div>}
          {active && <div className={classes.act}></div>}
          {nodes.map((node) => (
            <CcsonNode
              key={node.id}
              node={node}
              active={active && getNodeActions(node).length > 0}
              tileWidth={tileWidth}
              tileHeight={tileHeight}
            />
          ))}
        </div>
      </div>
      {meeple && <Meeple meeple={meeple} pos={meeplePos} active={active} onClick={handleMeepleClick} />}
    </div>
  );
};

export default React.memo(TileItem);
